// #165 Bildirimler — "Gönder" (toplu push gönderimi) ve "Gönderilenler"
// (gönderim geçmişi + ulaşım/okunma takibi) sekmeleri.
//
// Hedef kitle = seçilen KULLANICI TİPLERİ (#806: mobil / distribütör / merkez). Kriterler
// (temsilci grubu / distribütör / direk temsilci) KESİŞİM (VE) ile bağlanır; bir kriter boşsa
// o boyut uygulanmaz.
// Gönderim anında kritere uyan her kullanıcı için bir Notification kaydı üretilir
// ve aktif FCM token'larına anlık push gider.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { s, authHeaders, Toast } from './listPageStyles';
import { StatusBadge } from './ui';
import { Section, Grid, FieldCustom } from './detail/DetailShell';
import { MultiChipSelect } from './campaign/MultiChipSelect';
import { RELATION_CONFIGS } from './relationConfigs';

const unwrap = (p) => (p && (p.Data ?? p.data)) ?? p ?? {};

const TYPE_OPTIONS = [
  { value: 'SYSTEM', label: 'Sistem' },
  { value: 'CAMPAIGN', label: 'Kampanya' },
  { value: 'ORDER_STATUS', label: 'Sipariş' },
  { value: 'TASK_ASSIGNED', label: 'Görev' },
  { value: 'POINT_OPENING', label: 'Nokta Açma' },
  { value: 'TRAINING', label: 'Eğitim' },
];
const TYPE_LABEL = Object.fromEntries(TYPE_OPTIONS.map((o) => [o.value, o.label]));

// #806 Hedeflenebilir kullanıcı tipleri. Backend karşılığı BroadcastCriteria.UserTypes:
//   MOBILE = temsilciye bağlı saha kullanıcısı · DISTRIBUTOR = distribütör kullanıcısı ·
//   OFFICE = merkez/Back Office (ikisi de değil). Bildirim yalnız mobile gitmek zorunda değil.
const USER_TYPE_OPTIONS = [
  { value: 'MOBILE', label: 'Mobil (Saha)', hint: 'Temsilciye bağlı saha kullanıcıları' },
  { value: 'DISTRIBUTOR', label: 'Distribütör', hint: 'Distribütör tipi kullanıcılar' },
  { value: 'OFFICE', label: 'Merkez', hint: 'Back Office kullanıcıları' },
];
const USER_TYPE_LABEL = Object.fromEntries(USER_TYPE_OPTIONS.map((o) => [o.value, o.label]));

// #806/2 Kullanıcı tipi seçici. Eskiden yan yana dizilmiş üç ham checkbox'tı — işlevi doğruydu
// ama üst bardaki diğer alanlarla aynı görsel dile ait değildi. Artık seçilebilir "chip"
// (segment) düğmeleri: seçili olan vurgu rengiyle dolu, seçili olmayan çerçeveli.
function UserTypeChips({ value, onChange }) {
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', minHeight: 38 }}>
      {USER_TYPE_OPTIONS.map((o) => {
        const on = value.includes(o.value);
        return (
          <button
            key={o.value}
            type="button"
            title={o.hint}
            aria-pressed={on}
            onClick={() => toggle(o.value)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '7px 13px', borderRadius: 999, fontSize: 13,
              fontWeight: on ? 600 : 500, cursor: 'pointer',
              border: `1px solid ${on ? 'var(--primary, #185fa5)' : 'var(--border-color, #cbd5e1)'}`,
              background: on ? 'var(--primary, #185fa5)' : 'var(--input-bg, #fff)',
              color: on ? '#fff' : 'var(--text-secondary, #475569)',
              transition: 'background 120ms ease, color 120ms ease, border-color 120ms ease',
            }}
          >
            <span aria-hidden="true" style={{ fontSize: 12, lineHeight: 1 }}>{on ? '✓' : '+'}</span>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function formatDate(value) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('tr-TR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

const inputStyle = {
  width: '100%', padding: '9px 11px', borderRadius: 8, fontSize: 14,
  border: '1px solid var(--border-color, #cbd5e1)', boxSizing: 'border-box',
  background: 'var(--input-bg, #fff)', color: 'var(--text-primary, #0f172a)',
};

// ────────────────────────────────────────────────────────────────────────────
// Gönder
// ────────────────────────────────────────────────────────────────────────────
export function NotificationComposeTab({ onSent }) {
  const [type, setType] = useState('SYSTEM');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  // #806 Hedef kullanıcı tipleri. Eskiden alan "MOBILE"a kilitliydi (disabled select) ve Back
  // Office kullanıcılarına bildirim göndermek mümkün değildi. Varsayılan mobil + distribütör:
  // ekranın bugüne kadarki davranışı budur, kullanıcı isterse merkezi de ekler.
  const [userTypes, setUserTypes] = useState(['MOBILE', 'DISTRIBUTOR']);
  const [groupItems, setGroupItems] = useState([]);
  const [distItems, setDistItems] = useState([]);
  const [repItems, setRepItems] = useState([]);
  // #806/1 Kullanıcı ROL kriteri — çoklu seçim (birden fazla rol kriter olarak verilebilir).
  const [roleItems, setRoleItems] = useState([]);

  const [preview, setPreview] = useState({ recipientCount: null, deviceCount: null, loading: false });
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((tType, message) => {
    setToast({ type: tType, message });
    window.setTimeout(() => setToast(null), 3500);
  }, []);

  const criteria = useMemo(() => ({
    userTypes,
    // Role.Id int'tir; seçici kaynağa göre string döndürebildiği için sayıya zorlanır
    // (System.Text.Json varsayılanı string→int dönüşümü YAPMAZ, istek 400 dönerdi).
    roleIds: roleItems.map((x) => Number(x.value)).filter((n) => Number.isFinite(n)),
    groupIds: groupItems.map((x) => x.value),
    distributorIds: distItems.map((x) => x.value),
    representativeIds: repItems.map((x) => x.value),
  }), [userTypes, roleItems, groupItems, distItems, repItems]);

  // Kritere uyan alıcı/cihaz sayısını canlı (debounce) önizle.
  const previewKey = JSON.stringify(criteria);
  useEffect(() => {
    let alive = true;
    setPreview((p) => ({ ...p, loading: true }));
    const t = setTimeout(() => {
      fetch('/api/notifications/broadcast/preview', {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(criteria),
      })
        .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
        .then((d) => {
          if (!alive) return;
          const data = unwrap(d);
          setPreview({ recipientCount: data.recipientCount ?? 0, deviceCount: data.deviceCount ?? 0, loading: false });
        })
        .catch(() => { if (alive) setPreview({ recipientCount: null, deviceCount: null, loading: false }); });
    }, 350);
    return () => { alive = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewKey]);

  const canSend = title.trim() && body.trim() && userTypes.length > 0 && !sending && (preview.recipientCount === null || preview.recipientCount > 0);

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) { showToast('error', 'Başlık ve içerik zorunludur.'); return; }
    setSending(true);
    try {
      const res = await fetch('/api/notifications/broadcast', {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ type, title: title.trim(), body: body.trim(), criteria }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = unwrap(payload)?.message || payload?.Message || `Gönderim başarısız (${res.status})`;
        throw new Error(msg);
      }
      const data = unwrap(payload);
      showToast('success', `${data.recipientCount} kullanıcıya gönderildi · ${data.pushedCount} cihaza push iletildi.`);
      setTitle(''); setBody('');
      setGroupItems([]); setDistItems([]); setRepItems([]); setRoleItems([]);
      onSent?.();
    } catch (e) {
      showToast('error', e.message || 'Gönderim başarısız');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="dps-form" style={{ paddingTop: 4 }}>
      <Section icon="mail" title="Bildirim İçeriği"
        subtitle="Seçtiğiniz kullanıcı tiplerine anlık bildirim gönderin. İçerik şimdilik metin olarak iletilir."
      >
        <Grid cols={2}>
          <FieldCustom label="Bildirim Tipi">
            <select style={inputStyle} value={type} onChange={(e) => setType(e.target.value)}>
              {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </FieldCustom>
          <FieldCustom label="Kullanıcı Tipi" req>
            <UserTypeChips value={userTypes} onChange={setUserTypes} />
          </FieldCustom>
          <FieldCustom label="Başlık" span req>
            <input style={inputStyle} value={title} maxLength={120}
              onChange={(e) => setTitle(e.target.value)} placeholder="Bildirim başlığı" />
          </FieldCustom>
          <FieldCustom label="Mesaj" span req>
            <textarea style={{ ...inputStyle, minHeight: 96, resize: 'vertical' }} value={body} maxLength={1000}
              onChange={(e) => setBody(e.target.value)} placeholder="Bildirim metni" />
          </FieldCustom>
        </Grid>
      </Section>

      <Section icon="users" title="Hedef Kitle"
        subtitle="Tüm kriterler VE ile bağlanır; bir kriter boşsa o boyut uygulanmaz. Çoklu seçimlerde (rol, grup…) seçenekler kendi aralarında VEYA ile çalışır."
        right={
          <span style={{ fontSize: 13, color: 'var(--text-secondary, #64748b)' }}>
            {preview.loading ? 'Hesaplanıyor…' : preview.recipientCount === null ? '—' : (
              <>Alıcı: <b style={{ color: '#16a34a' }}>{preview.recipientCount}</b> · Cihazlı: <b>{preview.deviceCount}</b></>
            )}
          </span>
        }
      >
        <Grid cols={2}>
          {/* #806/1 Kullanıcı rolü kriteri. Merkez/Back Office kitlesini daraltmanın asıl yolu
              budur: "Merkez" tipi + 3-4 rol seçilerek yalnız ilgili ekipler hedeflenir. */}
          <FieldCustom label="Kullanıcı Rolü" span>
            <MultiChipSelect items={roleItems} onChange={setRoleItems}
              config={RELATION_CONFIGS.role} placeholder="Rol ara ve ekle (birden fazla seçilebilir)…" />
          </FieldCustom>
          <FieldCustom label="Temsilci Grubu">
            <MultiChipSelect items={groupItems} onChange={setGroupItems}
              config={RELATION_CONFIGS.representativeGroup} placeholder="Temsilci grubu ara ve ekle…" />
          </FieldCustom>
          {/* #819 Backend `DistributorIds`i UserDataScope.ScopeType==1 ile eşler; ScopeType 1 = Vendor
              (bkz. UserDetailPage SCOPE_DEFS). Bu yüzden seçici Vendor (Distribütör) tanımlarını
              listelemeli — Dağıtıcı (Distributor) DEĞİL. Önceden /api/distributors'a bağlıydı ve
              gönderilen id'ler hiçbir kullanıcıyla eşleşmiyordu. */}
          <FieldCustom label="Distribütör">
            <MultiChipSelect items={distItems} onChange={setDistItems}
              config={RELATION_CONFIGS.vendor} placeholder="Distribütör ara ve ekle…" />
          </FieldCustom>
          <FieldCustom label="Direk Temsilci" span>
            <MultiChipSelect items={repItems} onChange={setRepItems}
              config={RELATION_CONFIGS.representative} placeholder="Temsilci ara ve ekle…" />
          </FieldCustom>
        </Grid>
      </Section>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 }}>
        {preview.recipientCount === 0 && (
          <span style={{ alignSelf: 'center', fontSize: 13, color: '#b45309' }}>
            Seçilen kriterlere uyan kullanıcı yok.
          </span>
        )}
        <button type="button"
          style={{ ...s.btnPrimary, opacity: canSend ? 1 : 0.55, cursor: canSend ? 'pointer' : 'not-allowed' }}
          onClick={handleSend} disabled={!canSend}>
          {sending ? 'Gönderiliyor…' : 'Bildirimi Gönder'}
        </button>
      </div>

      <Toast toast={toast} />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Gönderilenler + ulaşım/okunma takibi
// ────────────────────────────────────────────────────────────────────────────
export function NotificationSentTab({ reloadKey }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [detail, setDetail] = useState(null); // seçilen broadcast

  const showToast = useCallback((tType, message) => {
    setToast({ type: tType, message });
    window.setTimeout(() => setToast(null), 3500);
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/notifications/broadcasts?take=200', { headers: authHeaders() });
      if (!res.ok) throw new Error(`Liste alınamadı (${res.status})`);
      const data = unwrap(await res.json());
      setRows(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      showToast('error', e.message || 'Liste alınamadı');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchList(); }, [fetchList, reloadKey]);

  return (
    <div style={{ paddingTop: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10, gap: 10 }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>Gönderilen Bildirimler</h3>
        <button style={s.btnSecondary} onClick={fetchList}>Yenile</button>
        <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--text-secondary, #64748b)' }}>{rows.length} gönderim</span>
      </div>

      <div style={{ overflowX: 'auto', border: '1px solid var(--border-color, #e2e8f0)', borderRadius: 10 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
          <thead>
            <tr style={{ background: 'var(--bg-subtle, #f8fafc)', textAlign: 'left' }}>
              {['Tarih', 'Başlık', 'Tip', 'Alıcı', 'Cihazlı', 'Push', 'Okunma', ''].map((h, i) => (
                <th key={i} style={{ padding: '10px 12px', fontWeight: 600, whiteSpace: 'nowrap', borderBottom: '1px solid var(--border-color, #e2e8f0)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} style={{ padding: 18, textAlign: 'center', color: '#64748b' }}>Yükleniyor…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={8} style={{ padding: 18, textAlign: 'center', color: '#64748b' }}>Henüz gönderim yok.</td></tr>}
            {!loading && rows.map((r) => {
              const recipient = r.recipientCount ?? r.RecipientCount ?? 0;
              const read = r.readCount ?? r.ReadCount ?? 0;
              const pct = recipient ? Math.round((read / recipient) * 100) : 0;
              return (
                <tr key={r.id ?? r.Id} style={{ borderBottom: '1px solid var(--border-color, #f1f5f9)' }}>
                  <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>{formatDate(r.createdDate ?? r.CreatedDate)}</td>
                  <td style={{ padding: '9px 12px' }}>
                    <div style={{ fontWeight: 600 }}>{r.title ?? r.Title}</div>
                    <div style={{ fontSize: 11.5, color: '#6b7280', maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.body ?? r.Body}</div>
                  </td>
                  <td style={{ padding: '9px 12px' }}><StatusBadge tone="neutral">{TYPE_LABEL[r.type ?? r.Type] || r.type || '-'}</StatusBadge></td>
                  <td style={{ padding: '9px 12px' }}>{recipient}</td>
                  <td style={{ padding: '9px 12px' }}>{r.deviceCount ?? r.DeviceCount ?? 0}</td>
                  <td style={{ padding: '9px 12px' }}>{r.pushedCount ?? r.PushedCount ?? 0}</td>
                  <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>
                    <span style={{ fontWeight: 600, color: pct >= 50 ? '#16a34a' : '#64748b' }}>{read}/{recipient}</span>
                    <span style={{ fontSize: 11.5, color: '#94a3b8' }}> ({pct}%)</span>
                  </td>
                  <td style={{ padding: '9px 12px' }}>
                    <button style={s.btnSecondary} onClick={() => setDetail(r)}>Detay</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {detail && <BroadcastDetailModal broadcast={detail} onClose={() => setDetail(null)} />}
      <Toast toast={toast} />
    </div>
  );
}

// Gönderim detayı: Özet + Ulaşım/Okunma sekmeleri.
function BroadcastDetailModal({ broadcast, onClose }) {
  const [tab, setTab] = useState('summary');
  const [recipients, setRecipients] = useState(null);
  const [stats, setStats] = useState({ total: 0, readCount: 0, deviceCount: 0 });
  const [loading, setLoading] = useState(false);
  const id = broadcast.id ?? broadcast.Id;

  useEffect(() => {
    if (tab !== 'recipients' || recipients !== null) return;
    setLoading(true);
    fetch(`/api/notifications/broadcasts/${id}/recipients`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => {
        const data = unwrap(d);
        setRecipients(Array.isArray(data.items) ? data.items : []);
        setStats({ total: data.total ?? 0, readCount: data.readCount ?? 0, deviceCount: data.deviceCount ?? 0 });
      })
      .catch(() => setRecipients([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  let criteria = {};
  try { criteria = JSON.parse(broadcast.criteriaJson ?? broadcast.CriteriaJson ?? '{}') || {}; } catch { criteria = {}; }
  const critCount = (arr) => (Array.isArray(arr) ? arr.length : 0);

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={{ ...s.modal, maxWidth: 760 }} onClick={(e) => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <h3 style={s.modalTitle}>{broadcast.title ?? broadcast.Title}</h3>
          <button type="button" style={s.closeBtn} onClick={onClose}>×</button>
        </div>

        <div style={{ display: 'flex', gap: 4, padding: '0 18px', borderBottom: '1px solid var(--border-color, #e2e8f0)' }}>
          {[{ k: 'summary', l: 'Özet' }, { k: 'recipients', l: 'Ulaşım / Okunma' }].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} style={{
              padding: '10px 14px', border: 'none', background: 'transparent', cursor: 'pointer',
              fontSize: 13.5, fontWeight: 600,
              color: tab === t.k ? 'var(--primary, #185fa5)' : '#64748b',
              borderBottom: tab === t.k ? '2px solid var(--primary, #185fa5)' : '2px solid transparent',
            }}>{t.l}</button>
          ))}
        </div>

        <div style={{ ...s.modalBody, maxHeight: '60vh', overflowY: 'auto' }}>
          {tab === 'summary' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={s.formRow}>
                <div style={s.formGroup}><label style={s.label}>Tip</label><div style={s.inputView}>{TYPE_LABEL[broadcast.type ?? broadcast.Type] || broadcast.type}</div></div>
                <div style={s.formGroup}><label style={s.label}>Gönderim</label><div style={s.inputView}>{formatDate(broadcast.createdDate ?? broadcast.CreatedDate)}</div></div>
              </div>
              <div style={s.formGroup}><label style={s.label}>Mesaj</label><div style={s.textareaView}>{broadcast.body ?? broadcast.Body}</div></div>
              <div style={s.formRow}>
                <div style={s.formGroup}><label style={s.label}>Alıcı</label><div style={s.inputView}>{broadcast.recipientCount ?? broadcast.RecipientCount ?? 0}</div></div>
                <div style={s.formGroup}><label style={s.label}>Cihazlı</label><div style={s.inputView}>{broadcast.deviceCount ?? broadcast.DeviceCount ?? 0}</div></div>
                <div style={s.formGroup}><label style={s.label}>Push İletilen</label><div style={s.inputView}>{broadcast.pushedCount ?? broadcast.PushedCount ?? 0}</div></div>
                <div style={s.formGroup}><label style={s.label}>Okuyan</label><div style={s.inputView}>{broadcast.readCount ?? broadcast.ReadCount ?? 0}</div></div>
              </div>
              {/* #806 Özet artık KULLANICI TİPİNİ de gösterir. Eskiden tip kriteri hiç yazılmıyor
                  ve diğer kriterler boşken sabit "Tüm mobil kullanıcılar" deniyordu — merkeze
                  gönderilmiş bir bildirim bile "mobil" görünüyordu. */}
              <div style={s.formGroup}>
                <label style={s.label}>Kullanıcı Tipi</label>
                <div style={s.inputView}>
                  {(() => {
                    // Eski kayıtlarda tip kriteri yok; o zamanki kitle mobil + distribütördü.
                    const types = Array.isArray(criteria.userTypes) && criteria.userTypes.length > 0
                      ? criteria.userTypes
                      : String(broadcast.audienceType ?? broadcast.AudienceType ?? 'MOBILE,DISTRIBUTOR').split(',');
                    return types.map((t) => USER_TYPE_LABEL[String(t).trim().toUpperCase()] || t).join(' · ');
                  })()}
                </div>
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Hedef Kitle Kriterleri</label>
                <div style={s.inputView}>
                  {critCount(criteria.roleIds) === 0 && critCount(criteria.groupIds) === 0 && critCount(criteria.distributorIds) === 0 && critCount(criteria.representativeIds) === 0
                    ? 'Ek kriter yok — seçilen tipteki tüm kullanıcılar'
                    : [
                        critCount(criteria.roleIds) && `${critCount(criteria.roleIds)} rol`,
                        critCount(criteria.groupIds) && `${critCount(criteria.groupIds)} temsilci grubu`,
                        critCount(criteria.distributorIds) && `${critCount(criteria.distributorIds)} distribütör`,
                        critCount(criteria.representativeIds) && `${critCount(criteria.representativeIds)} direk temsilci`,
                      ].filter(Boolean).join(' VE ')}
                </div>
              </div>
            </div>
          )}

          {tab === 'recipients' && (
            <div>
              <div style={{ display: 'flex', gap: 16, marginBottom: 10, fontSize: 13 }}>
                <span>Toplam: <b>{stats.total}</b></span>
                <span>Cihazlı: <b>{stats.deviceCount}</b></span>
                <span>Okuyan: <b style={{ color: '#16a34a' }}>{stats.readCount}</b></span>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'var(--bg-subtle, #f8fafc)', textAlign: 'left' }}>
                    {['Kullanıcı', 'Cihaz', 'Durum', 'Okunma'].map((h, i) => (
                      <th key={i} style={{ padding: '8px 10px', fontWeight: 600, borderBottom: '1px solid var(--border-color, #e2e8f0)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan={4} style={{ padding: 16, textAlign: 'center', color: '#64748b' }}>Yükleniyor…</td></tr>}
                  {!loading && recipients && recipients.length === 0 && <tr><td colSpan={4} style={{ padding: 16, textAlign: 'center', color: '#64748b' }}>Alıcı yok.</td></tr>}
                  {!loading && recipients && recipients.map((r) => (
                    <tr key={r.notificationId ?? r.NotificationId} style={{ borderBottom: '1px solid var(--border-color, #f1f5f9)' }}>
                      <td style={{ padding: '8px 10px' }}>
                        <div style={{ fontWeight: 600 }}>{r.userName ?? r.UserName ?? `#${r.userId ?? r.UserId}`}</div>
                        {(r.userEmail ?? r.UserEmail) && <div style={{ fontSize: 11, color: '#6b7280' }}>{r.userEmail ?? r.UserEmail}</div>}
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        {(r.hasDevice ?? r.HasDevice)
                          ? <StatusBadge tone="ok">Var</StatusBadge>
                          : <StatusBadge tone="neutral">Yok</StatusBadge>}
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        {(r.isRead ?? r.IsRead)
                          ? <StatusBadge tone="neutral">Okundu</StatusBadge>
                          : <StatusBadge tone="info">İletildi</StatusBadge>}
                      </td>
                      <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>{(r.isRead ?? r.IsRead) ? formatDate(r.readAt ?? r.ReadAt) : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div style={s.modalFooter}>
          <button type="button" style={s.btnSecondary} onClick={onClose}>Kapat</button>
        </div>
      </div>
    </div>
  );
}
